/**
 * Pointer maths for touch control — pure, so it can be tested without a browser.
 *
 * Coordinates are always **normalised** (0..1) and never device pixels. Fractions remove every
 * coordinate-space trap at once: scrcpy's video size, the canvas size and screen rotation. scrcpy
 * silently drops touch events whose coordinate space does not match its video size exactly, and
 * that failure looks like "the phone ignores me", not like a units bug.
 */

// A type alias, not an interface, so it can be handed straight to the input channel: TypeScript
// gives object *type literals* an implicit index signature and interfaces none.
export type NormPoint = {
  nx: number
  ny: number
}

export interface Rect {
  left: number
  top: number
  width: number
  height: number
}

export function normalise(clientX: number, clientY: number, rect: Rect): NormPoint {
  const nx = clamp01((clientX - rect.left) / rect.width)
  const ny = clamp01((clientY - rect.top) / rect.height)
  return { nx, ny }
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0
  return Math.min(1, Math.max(0, v))
}

export interface Velocity {
  vnx: number
  vny: number
}

/**
 * Measures flick speed in the browser, where timing is honest, and reports it with `touch_up` so
 * the hub can replay the gesture locally at the right speed.
 *
 * Without this the gesture reached the phone stretched out over the network, Android read it as a
 * slow drag, and a flick to open the notification shade started and snapped back.
 */
export class FlickTracker {
  private track: Array<NormPoint & { t: number }> = []

  constructor(private readonly window = 6) {}

  reset(): void {
    this.track = []
  }

  sample(p: NormPoint, t: number): void {
    this.track.push({ ...p, t })
    if (this.track.length > this.window) this.track.shift()
  }

  /** Normalised units per second, from the oldest kept sample to the newest. */
  velocity(): Velocity {
    const a = this.track[0]
    const b = this.track[this.track.length - 1]
    if (!a || !b) return { vnx: 0, vny: 0 }
    const dt = (b.t - a.t) / 1000
    // Under ~8 ms the timestamps are noise, and dividing by them invents huge velocities.
    if (dt <= 0.008) return { vnx: 0, vny: 0 }
    return { vnx: (b.nx - a.nx) / dt, vny: (b.ny - a.ny) / dt }
  }
}

/** ~30 sends/s is plenty over the network; every event is still sampled for velocity. */
export const MOVE_THROTTLE_MS = 33
